// src/components/operationCenter/widgets/TopMoversWidget.tsx
// Widget for top gainers/losers with filters

import React, { useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import type { WidgetProps, TopMoversConfig } from "@/types/dashboard";
import { TimeRangePicker } from "@/components/common";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import {
  useTokenCategoryMap,
  useTokensChanges,
  useTokensRollups,
} from "@/hooks/tokens/compare/useCompareData";
import { useMoversData } from "@/hooks/tokens/useMoversData";
import { MetricKeys } from "@/constants/metrics";
import { filterTokenRows } from "@/utils/tokens";
import {
  formatPct,
  getCellNumber,
  formatActualValue,
} from "@/utils/tokens/movers";
import type { WinKey, TokenRow, MoveItem } from "@/types/movers";
import type { CategoryTreeNode } from "@/types/dashboards/categories";

type MoveDirection = "gainers" | "losers";
type TimeRange = "1d" | "7d" | "30d" | "365d";

interface MetricOption {
  key: string;
  label: string;
}

const METRIC_OPTIONS: MetricOption[] = [
  { key: MetricKeys.PriceClose, label: "Price" },
  { key: MetricKeys.Volume, label: "Volume" },
  { key: MetricKeys.TVL, label: "TVL" },
  { key: MetricKeys.MarketCap, label: "Market Cap" },
];

const TopMoversWidget: React.FC<WidgetProps<TopMoversConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as TopMoversConfig;

  // State - initialized from config
  const [direction, setDirection] = useState<MoveDirection>(
    config.direction ?? "gainers"
  );
  const [range, setRange] = useState<TimeRange>(
    (config.time_range as TimeRange) ?? "1d"
  );
  const [selectedMetric, setSelectedMetric] = useState<string>(
    MetricKeys.PriceClose
  );
  const [category, setCategory] = useState<string>(
    config.sector_filter ?? "all"
  );

  // Data fetching
  const { data: currentRows } = useTokensScreenerData();
  const rows = useMemo(
    () => (Array.isArray(currentRows) ? currentRows : []),
    [currentRows]
  );
  const { roots: categoryRoots, maps } = useCategoriesTree();
  const { data: tokenCategoryMap } = useTokenCategoryMap();
  const { changeByTicker } = useTokensChanges();
  const { rollupByTicker } = useTokensRollups();

  // Filter rows
  const rowsFiltered = useMemo(
    () =>
      filterTokenRows(rows as TokenRow[], {
        search: "",
        mcMin: "",
        mcMax: "",
        category,
        maps,
        tokenCategoryMap:
          (tokenCategoryMap as unknown as Record<string, number[]>) || {},
        excludeStables: true,
        getTicker: (r) => String(r["tokenSymbol"] ?? ""),
        getName: (r) => String(r["tokenName"] ?? r["name"] ?? ""),
        getMarketCap: (r) =>
          (r["market_cap"] as number | null) ??
          (r[MetricKeys.MarketCap] as number | null) ??
          null,
      }),
    [rows, category, maps, tokenCategoryMap]
  );

  const allowedTickers = useMemo(
    () =>
      new Set(
        rowsFiltered
          .map((r) => String(r["tokenSymbol"] ?? "").toUpperCase())
          .filter(Boolean)
      ),
    [rowsFiltered]
  );

  const rowByTicker = useMemo(() => {
    const m = new Map<string, TokenRow>();
    for (const r of rowsFiltered) {
      const ticker = String(r["tokenSymbol"] ?? "").toUpperCase();
      if (ticker) m.set(ticker, r);
    }
    return m;
  }, [rowsFiltered]);

  // Get movers data
  const { buildMoves } = useMoversData(
    rowsFiltered,
    allowedTickers,
    changeByTicker,
    rollupByTicker,
    range as WinKey
  );

  // Get movers based on selected metric
  const movesData = useMemo(
    () => buildMoves(selectedMetric),
    [buildMoves, selectedMetric]
  );
  const movers = useMemo<MoveItem[]>(() => {
    const items = direction === "gainers" ? movesData.top : movesData.bottom;
    return items.slice(0, config.limit ?? 5);
  }, [movesData, direction, config.limit]);

  // Helper to get actual value
  const getActualValue = (ticker: string): number | null => {
    const row = rowByTicker.get(ticker);
    if (!row) return null;
    return getCellNumber(row, selectedMetric);
  };

  // Handlers
  const handleDirectionChange = useCallback(
    (newDirection: MoveDirection) => {
      setDirection(newDirection);
      onConfigChange?.({ direction: newDirection });
    },
    [onConfigChange]
  );

  const handleRangeChange = useCallback(
    (newRange: TimeRange) => {
      setRange(newRange);
      onConfigChange?.({ time_range: newRange });
    },
    [onConfigChange]
  );

  const handleMetricChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedMetric(e.target.value);
    },
    []
  );

  const handleCategoryChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newCategory = e.target.value;
      setCategory(newCategory);
      if (newCategory !== "all") {
        onConfigChange?.({ sector_filter: newCategory });
      }
    },
    [onConfigChange]
  );

  // Get flat list of categories
  const categoryOptions = useMemo(() => {
    if (!categoryRoots) return [];
    const getLeaves = (node: CategoryTreeNode): CategoryTreeNode[] => {
      if (node.is_leaf) return [node];
      return (node.children || []).flatMap(getLeaves);
    };
    return categoryRoots
      .filter((c) => c.category_code !== "overview")
      .flatMap((root) => getLeaves(root));
  }, [categoryRoots]);

  // Determine title
  const title = direction === "gainers" ? "Top Gainers" : "Top Losers";
  const metricLabel =
    METRIC_OPTIONS.find((m) => m.key === selectedMetric)?.label ?? "Price";

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
          {title}
        </span>
        <div className="flex items-center bg-gray-800/50 border border-gray-700/50 rounded-lg p-0.5">
          <button
            className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
              direction === "gainers"
                ? "bg-emerald-500/20 text-emerald-300"
                : "text-gray-400 hover:text-gray-200"
            }`}
            onClick={() => handleDirectionChange("gainers")}
          >
            Gainers
          </button>
          <button
            className={`px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${
              direction === "losers"
                ? "bg-rose-500/20 text-rose-300"
                : "text-gray-400 hover:text-gray-200"
            }`}
            onClick={() => handleDirectionChange("losers")}
          >
            Losers
          </button>
        </div>
      </div>
      {/* Controls */}
      <div className="flex-shrink-0 flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-700/30">
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select
            value={selectedMetric}
            onChange={handleMetricChange}
            className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0"
          >
            {METRIC_OPTIONS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>

          <select
            value={category}
            onChange={handleCategoryChange}
            className="select select-xs bg-gray-800/60 border-gray-700/50 focus:border-blue-500/50 text-slate-200 text-[11px] h-6 min-h-0 max-w-[100px]"
          >
            <option value="all">All</option>
            {categoryOptions.map((cat) => (
              <option key={cat.category_code} value={cat.category_code}>
                {cat.category_name}
              </option>
            ))}
          </select>

          <TimeRangePicker
            options={["1d", "7d", "30d", "365d"] as const}
            value={range}
            onChange={(v) => handleRangeChange(v as TimeRange)}
            theme="blue"
          />
        </div>
      </div>

      {/* Column Headers */}
      <div className="flex-shrink-0 grid grid-cols-[1fr_auto_auto] gap-3 items-center px-3 py-1.5 text-[10px] font-medium text-slate-400 border-b border-gray-700/20">
        <div>Token</div>
        <div className="text-right w-20">{metricLabel}</div>
        <div className="text-right w-16">Change</div>
      </div>

      {/* Movers List */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <ul>
          {movers.length === 0 ? (
            <li className="py-4 text-center text-sm text-slate-400">
              No data available
            </li>
          ) : (
            movers.map((item) => {
              const actualValue = getActualValue(item.ticker);
              const valueText = formatActualValue(selectedMetric, actualValue);
              const moveText = formatPct(item.value);
              const valueColor =
                Number(item.value) >= 0 ? "text-emerald-400" : "text-rose-400";

              return (
                <li key={item.ticker} className="odd:bg-gray-900/20">
                  <Link
                    to={`/app/tokens/${item.ticker.toLowerCase()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="grid grid-cols-[1fr_auto_auto] gap-3 items-center py-1.5 px-3 hover:bg-gray-700/20 transition-colors block"
                  >
                    {/* Token info */}
                    <div className="flex items-center gap-2 min-w-0">
                      {item.logo ? (
                        <img
                          src={item.logo}
                          alt={item.ticker}
                          className="w-4 h-4 rounded-full flex-shrink-0"
                        />
                      ) : (
                        <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500/30 to-purple-500/30 flex-shrink-0" />
                      )}
                      <div className="truncate min-w-0">
                        <div className="text-xs font-medium text-slate-200 truncate leading-tight">
                          {item.name}
                        </div>
                        <div className="text-[10px] text-slate-400 truncate leading-tight">
                          {item.ticker}
                        </div>
                      </div>
                    </div>

                    {/* Value */}
                    <div className="text-right tabular-nums text-xs text-slate-200 font-medium w-20">
                      {valueText}
                    </div>

                    {/* Move */}
                    <div
                      className={`text-right tabular-nums text-xs font-semibold w-16 ${valueColor}`}
                    >
                      {moveText}
                    </div>
                  </Link>
                </li>
              );
            })
          )}
        </ul>
      </div>

      {/* Footer note */}
      <div className="flex-shrink-0 px-3 py-1.5 border-t border-gray-700/30 text-[10px] text-slate-500">
        % change {range}
      </div>
    </div>
  );
};

export default TopMoversWidget;
